import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Trash2,
  Edit3,
  ChevronLeft,
  ChevronRight,
  Loader2,
  UploadCloud,
  FileText,
  Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { isAxiosError } from 'axios';
import api from '../lib/api';
import Modal from '../components/ui/Modal';
import FileInput from '../components/ui/FileInput';

function getErrorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) return err.response?.data?.error?.message || err.message || fallback;
  return fallback;
}

const TIERS = ['free', 'pro_medium'] as const;

interface BookForm {
  title: { en: string; ne: string };
  subtitle: { en: string; ne: string };
  author: string;
  yearPublished: string;
  description: { en: string; ne: string };
  coverImageUrl: string;
  pdfUrl: string;
  pdfKey: string;
  fileSizeBytes: number;
  tier: string;
  price: number;
  currency: string;
  isActive: boolean;
}

const emptyForm = (): BookForm => ({
  title: { en: '', ne: '' },
  subtitle: { en: '', ne: '' },
  author: '',
  yearPublished: '',
  description: { en: '', ne: '' },
  coverImageUrl: '',
  pdfUrl: '',
  pdfKey: '',
  fileSizeBytes: 0,
  tier: 'free',
  price: 0,
  currency: 'NPR',
  isActive: true,
});

interface EditableBook {
  _id: string;
  title?: { en?: string; ne?: string };
  subtitle?: { en?: string; ne?: string };
  author?: string;
  yearPublished?: number;
  description?: { en?: string; ne?: string };
  coverImageUrl?: string;
  pdfUrl?: string;
  pdfKey?: string;
  fileSizeBytes?: number;
  tier?: string;
  price?: number;
  currency?: string;
  isActive?: boolean;
}

function toPayload(f: BookForm) {
  return {
    title: { en: f.title.en, ...(f.title.ne && { ne: f.title.ne }) },
    ...((f.subtitle.en || f.subtitle.ne) && {
      subtitle: {
        ...(f.subtitle.en && { en: f.subtitle.en }),
        ...(f.subtitle.ne && { ne: f.subtitle.ne }),
      },
    }),
    ...(f.author && { author: f.author }),
    ...(f.yearPublished && { yearPublished: Number(f.yearPublished) }),
    ...((f.description.en || f.description.ne) && {
      description: {
        ...(f.description.en && { en: f.description.en }),
        ...(f.description.ne && { ne: f.description.ne }),
      },
    }),
    coverImageUrl: f.coverImageUrl,
    pdfUrl: f.pdfUrl,
    pdfKey: f.pdfKey,
    ...(f.fileSizeBytes ? { fileSizeBytes: f.fileSizeBytes } : {}),
    tier: f.tier,
    price: f.price,
    currency: f.currency,
    isActive: f.isActive,
  };
}

function formatSize(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Direct uploader to /upload/book-pdf — bypasses the generic Files library
 * since book PDFs can be much larger (150MB) than that endpoint's 50MB cap. */
function BookPdfInput({
  pdfUrl,
  fileSizeBytes,
  onUploaded,
}: {
  pdfUrl: string;
  fileSizeBytes: number;
  onUploaded: (data: { url: string; key: string; fileSizeBytes: number }) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data: res } = await api.post('/upload/book-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded(res.data);
      toast.success('PDF uploaded');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to upload PDF'));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Book PDF *</label>
      <div className="flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2 input-field !py-2 text-gray-600 overflow-hidden">
          {pdfUrl ? (
            <>
              <Check size={14} className="text-success-500 shrink-0" />
              <span className="truncate text-xs">{pdfUrl.split('/').pop()}</span>
              {fileSizeBytes > 0 && (
                <span className="text-xs text-gray-400 shrink-0">
                  ({formatSize(fileSizeBytes)})
                </span>
              )}
            </>
          ) : (
            <span className="text-xs text-gray-400">No PDF uploaded yet</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="btn-secondary flex items-center gap-1.5 shrink-0 !py-2"
        >
          {uploading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : pdfUrl ? (
            <UploadCloud size={14} />
          ) : (
            <FileText size={14} />
          )}
          {uploading ? 'Uploading...' : pdfUrl ? 'Replace' : 'Upload'}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={handleFile}
      />
    </div>
  );
}

function BookFormFields({ form, setForm }: { form: BookForm; setForm: (f: BookForm) => void }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title (EN) *</label>
          <input
            type="text"
            value={form.title.en}
            onChange={(e) => setForm({ ...form, title: { ...form.title, en: e.target.value } })}
            className="input-field"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title (NE)</label>
          <input
            type="text"
            value={form.title.ne}
            onChange={(e) => setForm({ ...form, title: { ...form.title, ne: e.target.value } })}
            className="input-field"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Subtitle badge (EN) <span className="text-gray-400 font-normal">e.g. 2025, New</span>
          </label>
          <input
            type="text"
            value={form.subtitle.en}
            onChange={(e) =>
              setForm({ ...form, subtitle: { ...form.subtitle, en: e.target.value } })
            }
            className="input-field"
            placeholder="2025"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Subtitle badge (NE)
          </label>
          <input
            type="text"
            value={form.subtitle.ne}
            onChange={(e) =>
              setForm({ ...form, subtitle: { ...form.subtitle, ne: e.target.value } })
            }
            className="input-field"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Author</label>
          <input
            type="text"
            value={form.author}
            onChange={(e) => setForm({ ...form, author: e.target.value })}
            className="input-field"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Year published</label>
          <input
            type="number"
            value={form.yearPublished}
            onChange={(e) => setForm({ ...form, yearPublished: e.target.value })}
            className="input-field"
            min={1900}
            max={2100}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description (EN)</label>
          <textarea
            value={form.description.en}
            onChange={(e) =>
              setForm({ ...form, description: { ...form.description, en: e.target.value } })
            }
            className="input-field"
            rows={2}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description (NE)</label>
          <textarea
            value={form.description.ne}
            onChange={(e) =>
              setForm({ ...form, description: { ...form.description, ne: e.target.value } })
            }
            className="input-field"
            rows={2}
          />
        </div>
      </div>

      <FileInput
        label="Cover image *"
        value={form.coverImageUrl}
        onChange={(url) => setForm({ ...form, coverImageUrl: url })}
        accept="image"
      />

      <BookPdfInput
        pdfUrl={form.pdfUrl}
        fileSizeBytes={form.fileSizeBytes}
        onUploaded={(data) =>
          setForm({
            ...form,
            pdfUrl: data.url,
            pdfKey: data.key,
            fileSizeBytes: data.fileSizeBytes,
          })
        }
      />

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tier</label>
          <select
            value={form.tier}
            onChange={(e) => setForm({ ...form, tier: e.target.value })}
            className="input-field"
          >
            {TIERS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Price <span className="text-gray-400 font-normal">(0 = tier-only)</span>
          </label>
          <input
            type="number"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: Number(e.target.value) })}
            className="input-field"
            min={0}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
          <input
            type="text"
            value={form.currency}
            onChange={(e) => setForm({ ...form, currency: e.target.value })}
            className="input-field"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
        <button
          type="button"
          role="switch"
          aria-checked={form.isActive}
          onClick={() => setForm({ ...form, isActive: !form.isActive })}
          className={`flex items-center gap-3 w-full rounded-lg border px-3 py-2 transition-colors ${
            form.isActive ? 'border-success-500/30 bg-success-50' : 'border-gray-200 bg-gray-50'
          }`}
        >
          <span
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
              form.isActive ? 'bg-success-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                form.isActive ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </span>
          <span className="text-sm text-left">
            <span className={`font-medium ${form.isActive ? 'text-success-700' : 'text-gray-600'}`}>
              {form.isActive ? 'Active' : 'Inactive'}
            </span>
            <span className="block text-xs text-gray-400">
              {form.isActive
                ? 'Visible to students in the app'
                : 'Hidden from the app until reactivated'}
            </span>
          </span>
        </button>
      </div>
    </div>
  );
}

function CreateBookModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<BookForm>(emptyForm());

  const mutation = useMutation({
    mutationFn: async (data: BookForm) => {
      await api.post('/books', toPayload(data));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['books'] });
      toast.success('Book created');
      setForm(emptyForm());
      onClose();
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to create book')),
  });

  return (
    <Modal open={open} onClose={onClose} title="Add Book" variant="drawer">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!form.coverImageUrl) return toast.error('Cover image is required');
          if (!form.pdfUrl) return toast.error('Book PDF is required');
          mutation.mutate(form);
        }}
        className="space-y-4"
      >
        <BookFormFields form={form} setForm={setForm} />
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn-primary flex items-center gap-2"
          >
            {mutation.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Plus size={16} />
            )}
            Create
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditBookModal({
  open,
  onClose,
  book,
}: {
  open: boolean;
  onClose: () => void;
  book: EditableBook;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<BookForm>({
    title: { en: book.title?.en || '', ne: book.title?.ne || '' },
    subtitle: { en: book.subtitle?.en || '', ne: book.subtitle?.ne || '' },
    author: book.author || '',
    yearPublished: book.yearPublished ? String(book.yearPublished) : '',
    description: { en: book.description?.en || '', ne: book.description?.ne || '' },
    coverImageUrl: book.coverImageUrl || '',
    pdfUrl: book.pdfUrl || '',
    pdfKey: book.pdfKey || '',
    fileSizeBytes: book.fileSizeBytes || 0,
    tier: book.tier || 'free',
    price: book.price ?? 0,
    currency: book.currency || 'NPR',
    isActive: book.isActive ?? true,
  });

  const mutation = useMutation({
    mutationFn: async (data: BookForm) => {
      await api.put(`/books/${book._id}`, toPayload(data));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['books'] });
      toast.success('Book updated');
      onClose();
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to update book')),
  });

  return (
    <Modal open={open} onClose={onClose} title="Edit Book" variant="drawer">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!form.coverImageUrl) return toast.error('Cover image is required');
          if (!form.pdfUrl) return toast.error('Book PDF is required');
          mutation.mutate(form);
        }}
        className="space-y-4"
      >
        <BookFormFields form={form} setForm={setForm} />
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn-primary flex items-center gap-2"
          >
            {mutation.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Edit3 size={16} />
            )}
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function BooksPage() {
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [editBook, setEditBook] = useState<EditableBook | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['books', page],
    queryFn: async () => {
      const res = await api.get('/books', { params: { page, limit: 20 } });
      return res.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/books/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['books'] });
      toast.success('Book deactivated');
    },
    onError: () => toast.error('Failed to deactivate'),
  });

  const books = data?.data ?? [];
  const pagination = data?.pagination ?? { total: 0, page: 1, totalPages: 1 };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Books</h1>
          <p className="text-gray-500 mt-1">{pagination.total} total books</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2">
          <Plus size={16} /> Add Book
        </button>
      </div>

      <CreateBookModal open={showCreate} onClose={() => setShowCreate(false)} />
      {editBook && (
        <EditBookModal open={!!editBook} onClose={() => setEditBook(null)} book={editBook} />
      )}

      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-500">Book</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Author / Year</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Tier</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Price</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Status</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : books.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-400">
                    No books found
                  </td>
                </tr>
              ) : (
                books.map((book: EditableBook & { subtitle?: { en?: string } }) => (
                  <tr key={book._id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        {book.coverImageUrl && (
                          <img
                            src={book.coverImageUrl}
                            alt=""
                            className="w-9 h-12 object-cover rounded border border-gray-200 shrink-0"
                          />
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{book.title?.en}</p>
                          {book.subtitle?.en && (
                            <p className="text-xs text-gray-400">{book.subtitle.en}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {book.author || '—'}
                      {book.yearPublished ? ` · ${book.yearPublished}` : ''}
                    </td>
                    <td className="py-3 px-4">
                      <span className="badge bg-purple-50 text-purple-700">{book.tier}</span>
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {book.price ? `${book.currency || 'NPR'} ${book.price}` : '—'}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`badge ${book.isActive ? 'bg-success-50 text-success-700' : 'bg-gray-100 text-gray-600'}`}
                      >
                        {book.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEditBook(book)}
                          className="p-1 text-gray-400 hover:text-primary-600"
                          title="Edit"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          onClick={() => deleteMutation.mutate(book._id)}
                          className="p-1 text-gray-400 hover:text-danger-500"
                          title="Deactivate"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="btn-secondary text-xs flex items-center gap-1"
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= pagination.totalPages}
              className="btn-secondary text-xs flex items-center gap-1"
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
